import { useState } from "react";
import { Link } from "react-router-dom";
import { RecordLink } from "../../blocks/layout/RecordLink";
import { useGlobalReducer } from "../../../GlobalContext";
import { RecordCards } from "../../blocks/layout/RecordCards";
import { RecordTableColumn } from "../../blocks/layout/RecordTable.types";
import { Button } from "../../elements/Button";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { badgeClassName } from "../../elements/badgeClassName";
import { canSendAutomationExecutionNow } from "../automation/executions/automationExecutionRules";
import { statusVariants } from "../automation/automationExecutions";
import { DashboardExecution } from "./automationDashboard.types";

type Row = DashboardExecution;

const bareBackground = (background?: string) => background?.replace(/^bg-/, "");

function Badge({ text, variant }: { text: string; variant?: string }) {
  return <span className={badgeClassName(variant)}>{text}</span>;
}

function Badges({ row }: { row: Row }) {
  return (
    <span className="d-flex flex-wrap justify-content-end gap-1">
      <Badge
        text={row.statusLabel || row.statusName || "--"}
        variant={bareBackground(row.statusBackground) ?? statusVariants[row.statusName]}
      />
      {row.communicationStatus && (
        <Badge
          text={row.communicationStatus}
          variant={bareBackground(row.communicationStatusBackground)}
        />
      )}
      {row.modeStatus && (
        <Badge text={row.modeStatus} variant={bareBackground(row.modeStatusBackground)} />
      )}
    </span>
  );
}

function fixtureContext(row: Row) {
  const detail = [row.competitionName, row.teamName].filter(Boolean).join(" · ");

  return (
    <>
      <RecordLink href={row.fixtureLink?.to} text={row.fixtureName} newTab="button" />
      {detail && <span className="text-muted">{` · ${detail}`}</span>}
    </>
  );
}

const nameColumn: RecordTableColumn<Row> = {
  key: "name",
  label: "Automation",
  accessor: (row) => <RecordLink href={row.link?.to} text={row.name} newTab="button" />,
};

const badgeColumn: RecordTableColumn<Row> = {
  key: "id",
  label: "Status",
  cardSlot: "badge",
  accessor: (row) => <Badges row={row} />,
};

const contextColumns: RecordTableColumn<Row>[] = [
  { key: "fixtureName", label: "Fixture", cardSlot: "subtitle", accessor: fixtureContext },
  { key: "scheduleContext", label: "Schedule", cardSlot: "subtitle" },
];

const communicationColumn: RecordTableColumn<Row> = {
  key: "communicationCell",
  label: "Communication",
  accessor: (row) =>
    <RecordLink
      href={row.communicationName ? row.communicationLink?.to : undefined}
      text={row.communicationCell}
      newTab="button"
    />,
};

const audienceColumn: RecordTableColumn<Row> = {
  key: "audienceName",
  label: "Audience",
  accessor: (row) => <RecordLink href={row.audienceLink?.to} text={row.audienceName} newTab="button" />,
};

const lastErrorColumn: RecordTableColumn<Row> = {
  key: "lastError",
  label: "Message",
  accessor: (row) => <span className="text-danger">{row.lastError}</span>,
};

const upcomingColumns: RecordTableColumn<Row>[] = [
  nameColumn,
  badgeColumn,
  ...contextColumns,
  { key: "dueAt", label: "Send" },
  { key: "processingWindowStartsAt", label: "Processing starts" },
  communicationColumn,
  audienceColumn,
  lastErrorColumn,
];

const recentRunColumns: RecordTableColumn<Row>[] = [
  nameColumn,
  badgeColumn,
  ...contextColumns,
  { key: "dueAt", label: "Send" },
  { key: "completedOn", label: "Processing completed" },
  communicationColumn,
  audienceColumn,
  { key: "emailsSent", label: "Emails sent" },
  { key: "openRate", label: "Open rate" },
  { key: "clickRate", label: "Click rate" },
  { key: "clicksToOpenRate", label: "CTO" },
  lastErrorColumn,
];

// A run with no figures yet still shows them, as a dash, rather than dropping the line.
const BlankAsDash = ["completedOn", "emailsSent", "openRate", "clickRate", "clicksToOpenRate"];

// A fixture's name is its context, so the schedule only speaks for the rest; a
// communication falls back to when its content is to be copied.
const toCardRow = (row: Row): Row => ({
  ...row,
  ...Object.fromEntries(
    BlankAsDash.map((key) => [key, row[key] === undefined || row[key] === "" ? "-" : row[key]])
  ),
  scheduleContext: row.fixtureName ? "" : row.scheduleContext,
  communicationCell: row.communicationName || row.contentPreparationText,
});

export function RecentRunCards({ runs, emptyMessage }: { runs: Row[]; emptyMessage: string }) {
  return (
    <RecordCards
      columns={recentRunColumns}
      rows={runs.map(toCardRow)}
      emptyMessage={emptyMessage}
      rowKey={(row) => String(row.id || row.name)}
    />
  );
}

export interface UpcomingExecutionCardsProps {
  executions: Row[];
  emptyMessage: string;
  cancellingExecutionId: string | null;
  preparingExecutionId: string | null;
  onCancelClick: (execution: Row) => void;
  onPrepareCommunication: (execution: Row) => void;
  onAfterAction: () => void;
}

/** Upcoming executions as cards, with the buttons that update, send or cancel one. */
export function UpcomingExecutionCards({
  executions,
  emptyMessage,
  cancellingExecutionId,
  preparingExecutionId,
  onCancelClick,
  onPrepareCommunication,
  onAfterAction,
}: UpcomingExecutionCardsProps) {
  const globalDispatch = useGlobalReducer();
  const [sendNowExecution, setSendNowExecution] = useState<Row | null>(null);
  const [isSending, setIsSending] = useState(false);

  const sendNow = async () => {
    setIsSending(true);
    try {
      const { SendAutomationExecutionNow } = await import("../automation/automationService");
      const result = await SendAutomationExecutionNow(globalDispatch, sendNowExecution?.id);

      if (result?.response || result?.status >= 400) {
        return;
      }

      setSendNowExecution(null);
      await onAfterAction();
    } finally {
      setIsSending(false);
    }
  };

  const actions = (row: Row) => {
    const canSend = canSendAutomationExecutionNow(row);
    const canUpdate = row.updateCommunicationLink || row.canPrepare;

    if (!row.canCancel && !canUpdate && !canSend) {
      return null;
    }

    return (
      <>
        {row.canCancel && (
          <Button
            variant="outline-danger"
            className="me-auto"
            text="Cancel send"
            disabled={cancellingExecutionId === row.id}
            showSpinner={cancellingExecutionId === row.id}
            onClick={() => onCancelClick(row)}
          />
        )}
        {row.updateCommunicationLink ? (
          <Link
            className="btn btn-primary"
            to={row.updateCommunicationLink.to}
          >
            Update content
          </Link>
        ) : (
          row.canPrepare && (
            <Button
              variant="primary"
              text="Update content"
              disabled={isSending || preparingExecutionId === row.id}
              showSpinner={preparingExecutionId === row.id}
              onClick={() => onPrepareCommunication(row)}
            />
          )
        )}
        {canSend && (
          <Button
            variant="outline-primary"
            text="Send now"
            disabled={isSending}
            onClick={() => setSendNowExecution(row)}
          />
        )}
      </>
    );
  };

  return (
    <>
      <RecordCards
        columns={upcomingColumns}
        rows={executions.map(toCardRow)}
        emptyMessage={emptyMessage}
        rowActions={actions}
        rowKey={(row) => String(row.id)}
      />
      {sendNowExecution && (
        <ConfirmationModal
          modalTitle="Send automation now"
          confirmText="Send now"
          isSubmitting={isSending}
          onClose={() => !isSending && setSendNowExecution(null)}
          onConfirm={sendNow}
        >
          <p className="mb-0">
            Update this automation execution send time to now? The automation agent can pick it
            up on its next poll.
          </p>
        </ConfirmationModal>
      )}
    </>
  );
}
